// @ts-check
// UI store — Zustand (Overmind retirement, Phase C2 part 1). Pure, EPHEMERAL,
// cross-component UI state that has no cross-request meaning and is therefore
// NEVER persisted and NEVER seeded into the SSR HTML. Every field defaults to its
// "closed / empty" value, so the store renders identically on the server and on
// the client's first render (no #418/#423) with no hydration gate needed — unlike
// the cart/session stores there is nothing user-specific to keep out of the shared
// micro-cache (#188).
//
// These fields moved off Overmind because they are read by ONE component and
// written by ANOTHER (cross-component), so per-component useState cannot express
// them:
//   - showShoppingCartSidePanel — opened by the header cart button / add-to-cart /
//     checkout forks, read + closed by <ShoppingCartPanel>.
//   - showPreOrderWarningModal / preOrderWarning — opened by the product checkout
//     forks, read + closed by <PreOrderWarningModal>.
//   - navbarElementProps — written on every scroll tick by <NavScrollController>,
//     read by <SecondaryNavbar> (product sub-navbar pin offset).
//   - selectedHeaderCategory — the currently-open header dropdown (mutual
//     exclusion across every <CartNavCategoryIsland>).
//   - currentCarouselIndexes — the active slide per <HeaderGallery1> carousel,
//     advanced by a SINGLE global 12s interval that drives every registered
//     carousel at once (a page can mount several, e.g. /coaching).
//
// The action semantics below mirror the former Overmind actions/operators EXACTLY
// (grep overmind/actions.js + operators.js for each): cartNavItemClicked,
// hideShoppingCartSidePanel, shopPreOrderClicked, closePreOrderWarningModal,
// saveNavbarElementProps, updateSelectedHeaderCategory, addCarouselToState,
// carouselIndicatorClicked + the startCarouselInterval/resetCarouselInterval pair.
import { create } from 'zustand'

// The global carousel auto-advance interval id. Kept as a MODULE-LEVEL handle (the
// former Overmind `state.carouselInterval`) rather than store state because it is
// never rendered — only cleared/restarted — so it does not belong in React state.
let carouselIntervalId = null
const CAROUSEL_INTERVAL_MS = 12000

export const useUiStore = create((set, get) => ({
  // ---- cart side panel ----
  showShoppingCartSidePanel: false,

  // ---- pre-order warning modal ----
  showPreOrderWarningModal: false,
  preOrderWarning: { itemType: '', itemWarningText: '' },

  // ---- header ----
  /** @type {{ offsetTop?: number, offsetHeight?: number }} */
  navbarElementProps: {},
  /** @type {string|null} currently-open header dropdown category title */
  selectedHeaderCategory: null,

  // ---- HeaderGallery1 carousel(s) ----
  /** @type {Record<string, { currentIndex: number, length: number }>} */
  currentCarouselIndexes: {},

  // ---- actions (mirror the former Overmind action semantics EXACTLY) ----

  /** Open the slide-out cart panel. Mirrors actions.cartNavItemClicked. */
  openCart: () => set({ showShoppingCartSidePanel: true }),
  /** Close the slide-out cart panel. Mirrors actions.hideShoppingCartSidePanel. */
  hideCart: () => set({ showShoppingCartSidePanel: false }),

  /**
   * Open the pre-order warning modal for an item. Mirrors actions.shopPreOrderClicked:
   * set preOrderWarning.itemType/itemWarningText and flip the modal on.
   * @param {{ itemType?: string, itemWarningText?: string }} info
   */
  openPreOrderWarning: (info = {}) => set({
    showPreOrderWarningModal: true,
    preOrderWarning: {
      itemType: info.itemType || '',
      itemWarningText: info.itemWarningText || '',
    },
  }),
  /**
   * Close the pre-order warning modal AND reset its payload. Mirrors
   * actions.closePreOrderWarningModal (show=false + itemType/itemWarningText='').
   */
  closePreOrderWarning: () => set({
    showPreOrderWarningModal: false,
    preOrderWarning: { itemType: '', itemWarningText: '' },
  }),

  /**
   * Persist the main navbar's live offset/height so the product SecondaryNavbar can
   * pin under it. Mirrors actions.saveNavbarElementProps.
   * @param {{ offsetTop: number, offsetHeight: number }} p
   */
  saveNavbarElementProps: ({ offsetTop, offsetHeight }) =>
    set({ navbarElementProps: { offsetTop, offsetHeight } }),

  /**
   * Set the currently-open header dropdown category (or null to close all).
   * Mirrors actions.updateSelectedHeaderCategory.
   * @param {string|null} category
   */
  setSelectedHeaderCategory: (category) => set({ selectedHeaderCategory: category }),

  /**
   * Register a carousel so the global interval advances it. Mirrors
   * actions.addCarouselToState: only carousels with length > 1 are tracked, seeded
   * at index 0. Registering the first carousel lazily starts the global interval
   * (the former onInitializeOvermind -> startCarouselInterval, deferred to first
   * use so it never runs on the server).
   * @param {{ key: string, length: number }} p
   */
  registerCarousel: ({ key, length }) => {
    if (length > 1) {
      set((s) => ({
        currentCarouselIndexes: {
          ...s.currentCarouselIndexes,
          [key]: { currentIndex: 0, length },
        },
      }))
      ensureCarouselInterval(get)
    }
  },

  /**
   * Set a carousel's active slide from a USER interaction (indicator click) and
   * reset the auto-advance timer. Mirrors actions.carouselIndicatorClicked =
   * pipe(updateCurrentCarouselIndex, resetCarouselInterval): the manual pick
   * restarts the 12s countdown so it does not immediately jump again.
   * @param {{ key: string, currentIndex: number }} p
   */
  setCarouselIndex: ({ key, currentIndex }) => {
    set((s) => {
      const existing = s.currentCarouselIndexes[key]
      if (!existing) return {}
      return {
        currentCarouselIndexes: {
          ...s.currentCarouselIndexes,
          [key]: { ...existing, currentIndex },
        },
      }
    })
    resetCarouselInterval(get)
  },
}))

// ---- global carousel interval (module-private; mirrors operators.startCarousel-
// Interval / resetCarouselInterval). A single interval advances EVERY registered
// carousel every 12s, wrapping each back to 0 at its end — identical to the former
// setInterval whose callback fired carouselIndicatorClicked for each carousel. The
// auto-advance updates all indexes in one set() WITHOUT resetting the timer (the
// former per-tick reset-and-restart was a no-op churn — the observable cadence is
// the same steady 12s), while a manual setCarouselIndex DOES reset it. ----

/** @param {() => any} get */
function ensureCarouselInterval(get) {
  if (carouselIntervalId != null) return
  startCarouselInterval(get)
}

/** @param {() => any} get */
function startCarouselInterval(get) {
  if (typeof window === 'undefined') return
  carouselIntervalId = setInterval(() => {
    const indexes = get().currentCarouselIndexes
    const keys = Object.keys(indexes)
    if (!keys.length) return
    const next = {}
    keys.forEach((key) => {
      const c = indexes[key]
      const nextIndex = c.currentIndex + 1 >= c.length ? 0 : c.currentIndex + 1
      next[key] = { ...c, currentIndex: nextIndex }
    })
    // Auto-advance write: update every index in one commit WITHOUT resetting the
    // timer (steady 12s cadence).
    useUiStore.setState({ currentCarouselIndexes: next })
  }, CAROUSEL_INTERVAL_MS)
}

/** @param {() => any} get */
function resetCarouselInterval(get) {
  if (carouselIntervalId != null) {
    clearInterval(carouselIntervalId)
    carouselIntervalId = null
  }
  startCarouselInterval(get)
}

// ---- selectors ----
/** @param {{ showShoppingCartSidePanel: boolean }} s */
export const selectShowCart = (s) => s.showShoppingCartSidePanel
